<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { NButton, NEmpty, NInput, NSelect, NSpin, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import PromptLibraryRow from './PromptLibraryRow.vue'
import { useAgentPromptsStore, type AgentPrompt } from '../stores/agent_prompts'
import { useAgentReposStore } from '../stores/agent_repos'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin page to curate canned prompts for the /agent composer's new-session
// picker — either global (shown for every repo) or scoped to one
// repo by slug. Separate from AdminAgentRepos.vue per the parent PRD: a
// repo can accumulate several prompts, which would clutter the repo form.
const promptsStore = useAgentPromptsStore()
const reposStore = useAgentReposStore()
const message = useMessage()
useDocumentTitle('Prompt library')

interface PromptGroup {
  key: string
  label: string
  prompts: AgentPrompt[]
}

// naive-ui's NSelect option `value` is typed `string | number | undefined` —
// it has no `null` member, so "Global" can't be an option with `value: null`.
// `''` is the sentinel or empty repo_slug; converted to/from `null` only at
// the store-call boundary (a slug can never be empty, so no collision).
const GLOBAL_SCOPE = ''

const scopeOptions = computed(() => [
  { label: 'General (all repos)', value: GLOBAL_SCOPE },
  ...reposStore.repos.map((r) => ({ label: r.slug, value: r.slug })),
])

// Grouped for display — "General" first, then one section per repo that has
// at least one prompt, sorted by position within each group.
const groups = computed<PromptGroup[]>(() => {
  const bySlug = new Map<string | null, AgentPrompt[]>()
  for (const prompt of promptsStore.prompts) {
    const list = bySlug.get(prompt.repo_slug) ?? []
    list.push(prompt)
    bySlug.set(prompt.repo_slug, list)
  }
  const sortByPosition = (list: AgentPrompt[]) => list.toSorted((a, b) => a.position - b.position)

  const result: PromptGroup[] = []
  const global = bySlug.get(null)
  if (global) result.push({ key: 'global', label: 'General', prompts: sortByPosition(global) })
  for (const [slug, list] of bySlug) {
    if (slug === null) continue
    result.push({ key: slug, label: slug, prompts: sortByPosition(list) })
  }
  return result
})

// Client-side filter — with a dozen+ prompts across several repos,
// scrolling through every card to find one by eye doesn't scale. Matches
// against label or text, case-insensitive; groups with no matches are
// hidden entirely rather than shown empty.
const searchQuery = ref('')
const visibleGroups = computed<PromptGroup[]>(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return groups.value
  return groups.value
    .map((group) => ({
      ...group,
      prompts: group.prompts.filter(
        (p) => p.label.toLowerCase().includes(query) || p.text.toLowerCase().includes(query),
      ),
    }))
    .filter((group) => group.prompts.length > 0)
})
const noSearchMatches = computed(() => searchQuery.value.trim().length > 0 && visibleGroups.value.length === 0)

// Collapsed by default — with several prompts already on the page,
// a full-size add form permanently anchored at the bottom both buries the
// most-used "read/find a prompt" task under an always-open form AND makes
// adding a new one require scrolling past every existing card. Moving the
// trigger to the top and collapsing the form fixes both.
const addFormOpen = ref(false)
const addForm = reactive({ label: '', text: '', repo_slug: GLOBAL_SCOPE, saving: false })

onMounted(async () => {
  try {
    await Promise.all([promptsStore.fetchAll(), reposStore.fetchAll()])
  } catch {
    /* loadError surfaces in template */
  }
})

function findGroup(id: number): PromptGroup | undefined {
  return groups.value.find((g) => g.prompts.some((p) => p.id === id))
}

async function handleUpdate(id: number, params: { label?: string; text?: string }): Promise<void> {
  try {
    await promptsStore.update(id, params)
    message.success('Prompt updated')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to update prompt')
  }
}

async function handleScopeChange(id: number, repoSlug: string | null): Promise<void> {
  try {
    await promptsStore.update(id, { repo_slug: repoSlug })
    message.success('Scope updated')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to update scope')
  }
}

async function handleDestroy(id: number): Promise<void> {
  try {
    await promptsStore.destroy(id)
    message.success('Prompt removed')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to remove prompt')
  }
}

async function move(id: number, delta: 1 | -1): Promise<void> {
  const group = findGroup(id)
  if (!group) return
  const ids = group.prompts.map((p) => p.id)
  const index = ids.indexOf(id)
  const swapWith = index + delta
  if (swapWith < 0 || swapWith >= ids.length) return
  ;[ids[index], ids[swapWith]] = [ids[swapWith], ids[index]]
  try {
    await promptsStore.reorder(ids)
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to reorder prompts')
  }
}

async function addPrompt(): Promise<void> {
  if (!addForm.label.trim() || !addForm.text.trim()) {
    message.error('Label and text are required')
    return
  }
  addForm.saving = true
  try {
    await promptsStore.create({
      label: addForm.label.trim(),
      text: addForm.text.trim(),
      repo_slug: addForm.repo_slug || null,
    })
    addForm.label = ''
    addForm.text = ''
    addForm.repo_slug = GLOBAL_SCOPE
    message.success('Prompt added')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to add prompt')
  } finally {
    addForm.saving = false
  }
}
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-4 pb-6">
      <header class="flex items-start justify-between gap-3">
        <div class="flex flex-col gap-1">
          <h1 class="text-2xl font-semibold text-white m-0">Prompt library</h1>
          <p class="text-sm text-white/60 m-0">
            Canned prompts for the /agent composer's new-session picker. A prompt is either General (shown for every
            repo) or scoped to one repo.
          </p>
        </div>
        <NButton
          type="primary"
          secondary
          class="shrink-0"
          data-testid="prompt-add-toggle"
          @click="addFormOpen = !addFormOpen"
          >{{ addFormOpen ? 'Close' : '+ Add a prompt' }}</NButton
        >
      </header>

      <!-- Add prompt form — collapsed by default: with several prompts
           already on the page, an always-open form at the bottom both buried
           itself under every existing card and competed for attention with
           the read/find task most visits are actually for. -->
      <section
        v-if="addFormOpen"
        class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-3"
        data-testid="prompt-add-form"
      >
        <label class="flex flex-col gap-1 text-xs text-white/60">
          Label
          <NInput v-model:value="addForm.label" :disabled="addForm.saving" data-testid="prompt-add-label" />
        </label>
        <label class="flex flex-col gap-1 text-xs text-white/60">
          Text
          <NInput
            v-model:value="addForm.text"
            type="textarea"
            :rows="3"
            :disabled="addForm.saving"
            data-testid="prompt-add-text"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-white/60">
          Scope
          <NSelect
            v-model:value="addForm.repo_slug"
            :options="scopeOptions"
            :disabled="addForm.saving"
            data-testid="prompt-add-scope"
          />
        </label>
        <div class="flex justify-end">
          <NButton type="primary" :loading="addForm.saving" data-testid="prompt-add-submit" @click="addPrompt"
            >Add prompt</NButton
          >
        </div>
      </section>

      <div v-if="promptsStore.loading && !promptsStore.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <div v-else-if="promptsStore.loadError" class="text-red-300 text-sm" data-testid="prompt-library-load-error">
        Failed to load prompts: {{ promptsStore.loadError }}
      </div>

      <NEmpty v-else-if="promptsStore.prompts.length === 0" description="No prompts configured yet." class="my-12" />

      <template v-else>
        <NInput
          v-model:value="searchQuery"
          placeholder="Search prompts by label or text…"
          clearable
          data-testid="prompt-search"
        />

        <NEmpty
          v-if="noSearchMatches"
          description="No prompts match your search."
          class="my-8"
          data-testid="prompt-search-empty"
        />

        <div v-else class="flex flex-col gap-6" data-testid="prompt-library-groups">
          <section
            v-for="group in visibleGroups"
            :key="group.key"
            class="flex flex-col gap-2"
            :data-testid="`prompt-group-${group.key}`"
          >
            <h2 class="text-sm font-semibold text-white/70 m-0 uppercase tracking-wide">
              {{ group.label }} <span class="text-white/40 font-normal normal-case">({{ group.prompts.length }})</span>
            </h2>
            <ul class="list-none p-0 m-0 flex flex-col gap-2">
              <PromptLibraryRow
                v-for="(prompt, index) in group.prompts"
                :key="prompt.id"
                :prompt="prompt"
                :scope-options="scopeOptions"
                :is-first="index === 0"
                :is-last="index === group.prompts.length - 1"
                :on-update="handleUpdate"
                @scope-change="handleScopeChange"
                @destroy="handleDestroy"
                @move-up="move($event, -1)"
                @move-down="move($event, 1)"
              />
            </ul>
          </section>
        </div>
      </template>
    </section>
  </RouteShell>
</template>
